import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, LAST_PAGE_KEY, lastPage, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import ResetPassword from './components/ResetPassword';
import BronzeLayer from './components/BronzeLayer';
import SilverLayer from './components/SilverLayer';
import GoldLayer from './components/GoldLayer';
import Commands from './components/Commands';
import UserManagement from './components/UserManagement';
import TestNewLayout from './components/TestNewLayout';
import ClassificationReview from './components/ClassificationReview';
import TaskRouting from './components/TaskRouting';
import Mailbox from './components/Mailbox';
import OpsDigest from './components/OpsDigest';
import Layout from './components/Layout';
import JobAuditQueue from './components/JobAuditQueue';
import JobAuditDetail from './components/JobAuditDetail';
import JobAuditSummary from './components/JobAuditSummary';
import JobAuditWorkOn from './components/JobAuditWorkOn';
import JobAuditWorkOnTechnicians from './components/JobAuditWorkOnTechnicians';
import JobAuditScoringAdmin from './components/JobAuditScoringAdmin';
import JobAuditTechnicians from './components/JobAuditTechnicians';
import JobAuditTechnicianAudits from './components/JobAuditTechnicianAudits';
import JobAuditNotificationSettings from './components/JobAuditNotificationSettings';

// Where to send someone who lacks what a route needs, and where "/" itself goes.
// It can never return "/": that path is now a redirect to this function, so
// returning it would loop.
//
// Admins land on Time Analysis. The hasFeature guard on that first line is not
// redundant — /gold is feature-gated, so an admin arriving without
// time_analysis in the catalog would be bounced straight back here. Order
// below follows the nav.
const landingPath = (isAdmin, hasFeature) => {
  if (isAdmin() && hasFeature('time_analysis')) return '/gold';
  if (hasFeature('datasync')) return '/bronze';
  if (hasFeature('time_analysis')) return '/gold';
  if (hasFeature('service_inbox')) return '/review';
  if (hasFeature('audit')) return '/audit';
  if (isAdmin()) return '/users';
  return '/no-access';
};

// "/" renders no page of its own now; it forwards to the last page this browser
// was on (where a sign-in lands), else to whatever the account can actually use.
// Kept behind ProtectedRoute so an unauthenticated visitor still gets /login
// rather than a destination computed from an empty session.
const LandingRedirect = () => {
  const { isAdmin, hasFeature } = useAuth();
  return <Navigate to={lastPage() || landingPath(isAdmin, hasFeature)} replace />;
};

const NoAccess = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="max-w-md text-center">
      <h1 className="text-2xl font-semibold text-gray-900">No features enabled</h1>
      <p className="mt-2 text-sm text-gray-600">
        This account is active but has not been given access to anything yet. Ask an
        administrator to enable a feature for you.
      </p>
    </div>
  </div>
);

// Sign-in lands on "/", which returns to the last page this browser was on. A
// link opened while signed out (a Mailbox link in a ServiceTitan task, say) is
// saved as that page first, so signing in lands on it rather than elsewhere.
const RememberAndSignIn = () => {
  const location = useLocation();
  const target = `${location.pathname}${location.search}`;
  if (location.pathname !== '/') {
    try {
      localStorage.setItem(LAST_PAGE_KEY, target);
    } catch {
      /* no storage (private window): sign-in lands on the default page */
    }
  }
  return <Navigate to="/login" />;
};

const ProtectedRoute = ({ children, adminOnly = false, feature = null }) => {
  const { isAuthenticated, isAdmin, hasFeature, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <RememberAndSignIn />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to={landingPath(isAdmin, hasFeature)} replace />;
  }

  if (feature && !hasFeature(feature)) {
    return <Navigate to={landingPath(isAdmin, hasFeature)} replace />;
  }

  return children;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* "/" shows no page of its own: it forwards to the first thing the
                account can use, which for an admin is Time Analysis.
                Dashboard.js is left on disk but nothing routes to it. */}
            <Route path="/" element={
              <ProtectedRoute>
                <LandingRedirect />
              </ProtectedRoute>
            } />
            <Route path="/review" element={
              <ProtectedRoute feature="service_inbox">
                <Layout>
                  <ClassificationReview />
                </Layout>
              </ProtectedRoute>
            } />
            {/* Admin-only: routing edits are gated on the admin claim alone, and
                the page's data (/api/inbox/routing) is served by the service
                inbox, which enforces the same. */}
            <Route path="/routing" element={
              <ProtectedRoute adminOnly={true}>
                <Layout>
                  <TaskRouting />
                </Layout>
              </ProtectedRoute>
            } />
            {/* The live service mailbox, every message and body. Part of the
                service_inbox feature, like Review; the service inbox's
                /api/inbox/mailbox enforces the same. */}
            <Route path="/mailbox" element={
              <ProtectedRoute feature="service_inbox">
                <Layout>
                  <Mailbox />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit/summary/:itemId/technicians/:technicianId" element={
              <ProtectedRoute feature="audit">
                <Layout>
                  <JobAuditWorkOnTechnicians />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit/summary/:itemId/technicians" element={
              <ProtectedRoute feature="audit">
                <Layout>
                  <JobAuditWorkOnTechnicians />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit/summary/:itemId" element={
              <ProtectedRoute feature="audit">
                <Layout>
                  <JobAuditWorkOn />
                </Layout>
              </ProtectedRoute>
            } />
            {/* The Ops Dashboard (the SOW's ops digest, 3.C.2): what came in over a day or a week and what
                happened to it. service_inbox feature, like the Mailbox. */}
            <Route path="/digest" element={
              <ProtectedRoute feature="service_inbox">
                <Layout>
                  <OpsDigest />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit/summary" element={
              <ProtectedRoute feature="audit">
                <Layout>
                  <JobAuditSummary />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit/scoring" element={
              <ProtectedRoute feature="audit">
                <Layout>
                  <JobAuditScoringAdmin />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit/technicians/:technicianId" element={
              <ProtectedRoute feature="audit">
                <Layout>
                  <JobAuditTechnicianAudits />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit/technicians" element={
              <ProtectedRoute feature="audit">
                <Layout>
                  <JobAuditTechnicians />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit/notifications" element={
              <ProtectedRoute feature="audit" adminOnly={true}>
                <Layout>
                  <JobAuditNotificationSettings />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit/:auditRunId" element={
              <ProtectedRoute feature="audit">
                <Layout>
                  <JobAuditDetail />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/audit" element={
              <ProtectedRoute feature="audit">
                <Layout>
                  <JobAuditQueue />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/bronze" element={
              <ProtectedRoute feature="datasync">
                <Layout>
                  <BronzeLayer />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/silver" element={
              <ProtectedRoute feature="datasync">
                <Layout>
                  <SilverLayer />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/gold" element={
              <ProtectedRoute feature="time_analysis">
                <Layout>
                  <GoldLayer />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/users" element={
              <ProtectedRoute adminOnly={true}>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/commands" element={
              <ProtectedRoute adminOnly={true}>
                <Layout>
                  <Commands />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/test-layout" element={
              <ProtectedRoute>
                <TestNewLayout />
              </ProtectedRoute>
            } />
            <Route path="/no-access" element={
              <ProtectedRoute>
                <NoAccess />
              </ProtectedRoute>
            } />
            {/* Catch all route - "/" then forwards to whatever the account can use */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App; 